import { useState, useEffect, useCallback, useRef } from 'react';
import { C } from '@/lib/design';
import {
  fetchOrganosGobierno,
  fetchEntidadesGobernanza,
  crearOrganoGobierno,
  actualizarOrganoGobierno,
  desactivarOrganoGobierno,
  añadirMiembroOrgano,
  actualizarMiembroOrgano,
  cesarMiembroOrgano,
  eliminarMiembroOrgano,
  fetchPersonas,
  type OrganoGobierno,
  type MiembroOrganoGobierno,
  type EntidadLite,
  type PersonaResumen,
  type CrearOrganoInput,
  type CrearMiembroOrganoInput,
} from '../../../../shared/services/api';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { useI18n } from '../../../../../i18n';
import { AdminPageHeader } from '../../../../shared/components/admin';

// 0457 — Helpers de traduccion reutilizados en este archivo. El componente principal
// + subcomponentes AdminOrganosMiembros comparten acceso al bloque de tooltips y
// placeholders via estos helpers locales para no repetir el boilerplate.
function useAdmTooltips(): Record<string, string> {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  return ((adm as Record<string, unknown>).tooltips as Record<string, string> | undefined) ?? {};
}
function useAdmPlaceholdersOrganos(): Record<string, string> {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  return (
    ((adm as Record<string, unknown>).organosPlaceholders as Record<string, string> | undefined) ??
    {}
  );
}

// Par (organo, miembro) para los diálogos de confirmación que operan sobre
// miembros. Necesitamos ambos porque `cesarMiembroOrgano` / `eliminarMiembroOrgano`
// requieren el `organoId` además del `miembroId`.
type OrganoMiembroPair = { organo: OrganoGobierno; miembro: MiembroOrganoGobierno };

// Nombre visible de un miembro (persona interna o externo). Usado en los mensajes
// de ConfirmDialog y en el panel de miembros para evitar duplicar la lógica.
function getMiembroLabel(m: MiembroOrganoGobierno): string {
  if (m.persona) {
    return `${m.persona.nombre} ${m.persona.apellidos}`.trim();
  }
  return m.nombreExterno ?? '—';
}

const TIPO_ORGANO_SUGERENCIAS = [
  'Consejo de Administración',
  'Junta General de Accionistas',
  'Comité de Dirección',
  'Comité de Auditoría',
  'Comité de Riesgos',
  'Comité de Nombramientos',
];

// ═══════════════════════════════════════════════════════════════
// Órgano Modal — crear/editar
// ═══════════════════════════════════════════════════════════════

interface OrganoForm {
  entidadId: string;
  tipoOrgano: string;
  nombre: string;
  descripcion: string;
}

const emptyOrganoForm: OrganoForm = {
  entidadId: '',
  tipoOrgano: 'Consejo de Administración',
  nombre: '',
  descripcion: '',
};

function OrganoModal({
  organo,
  entidades,
  onClose,
  onSaved,
}: {
  organo: OrganoGobierno | null;
  entidades: EntidadLite[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const placeholders = useAdmPlaceholdersOrganos();
  const isEdit = organo !== null;
  const [form, setForm] = useState<OrganoForm>(() =>
    isEdit
      ? {
          entidadId: organo.entidadId,
          tipoOrgano: organo.tipoOrgano,
          nombre: organo.nombre,
          descripcion: organo.descripcion ?? '',
        }
      : { ...emptyOrganoForm, entidadId: entidades[0]?.id ?? '' },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof OrganoForm>(k: K, v: OrganoForm[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  const handleSave = async () => {
    if (!form.entidadId || !form.tipoOrgano.trim() || !form.nombre.trim()) {
      setError('Campos obligatorios: entidad, tipo, nombre');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload: CrearOrganoInput = {
        entidadId: form.entidadId,
        tipoOrgano: form.tipoOrgano.trim(),
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || undefined,
      };
      if (isEdit) {
        await actualizarOrganoGobierno(organo.id, payload);
        onSaved('Órgano actualizado');
      } else {
        await crearOrganoGobierno(payload);
        onSaved('Órgano creado');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalStyle, width: 560 }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? 'Editar órgano de gobierno' : 'Nuevo órgano de gobierno'}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lblStyle}>Entidad *</label>
            <select
              value={form.entidadId}
              onChange={(e) => set('entidadId', e.target.value)}
              style={selStyle}
            >
              <option value="">— Selecciona entidad —</option>
              {entidades.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.razonSocial} ({e.codigo})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={lblStyle}>Tipo de órgano *</label>
            <input
              type="text"
              value={form.tipoOrgano}
              onChange={(e) => set('tipoOrgano', e.target.value)}
              list="tipo-organo-sugerencias"
              style={inpStyle}
              maxLength={200}
            />
            <datalist id="tipo-organo-sugerencias">
              {TIPO_ORGANO_SUGERENCIAS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div>
            <label style={lblStyle}>Nombre *</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => set('nombre', e.target.value)}
              style={inpStyle}
              maxLength={300}
              placeholder={placeholders.nombre ?? 'Consejo de Administración de...'}
            />
          </div>

          <div>
            <label style={lblStyle}>Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => set('descripcion', e.target.value)}
              rows={3}
              style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }}
              maxLength={2000}
            />
          </div>

          {error && <div style={errorBoxStyle}>{error}</div>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} disabled={saving} style={btnSecStyle}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={btnPrimStyle}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Miembro Modal — crear/editar
// ═══════════════════════════════════════════════════════════════

interface MiembroForm {
  tipo: 'interna' | 'externa';
  personaId: string;
  nombreExterno: string;
  cargo: string;
  fechaNombramiento: string;
  fechaCese: string;
}

const emptyMiembroForm: MiembroForm = {
  tipo: 'interna',
  personaId: '',
  nombreExterno: '',
  cargo: '',
  fechaNombramiento: new Date().toISOString().slice(0, 10),
  fechaCese: '',
};

function MiembroModal({
  organo,
  miembro,
  personas,
  onClose,
  onSaved,
}: {
  organo: OrganoGobierno;
  miembro: MiembroOrganoGobierno | null;
  personas: PersonaResumen[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const placeholders = useAdmPlaceholdersOrganos();
  const isEdit = miembro !== null;
  const [form, setForm] = useState<MiembroForm>(() =>
    isEdit
      ? {
          tipo: miembro.personaId ? 'interna' : 'externa',
          personaId: miembro.personaId ?? '',
          nombreExterno: miembro.nombreExterno ?? '',
          cargo: miembro.cargo,
          fechaNombramiento: miembro.fechaNombramiento.slice(0, 10),
          fechaCese: miembro.fechaCese?.slice(0, 10) ?? '',
        }
      : { ...emptyMiembroForm },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof MiembroForm>(k: K, v: MiembroForm[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  const handleSave = async () => {
    if (form.tipo === 'interna' && !form.personaId) {
      setError('Selecciona una persona');
      return;
    }
    if (form.tipo === 'externa' && !form.nombreExterno.trim()) {
      setError('Nombre externo obligatorio');
      return;
    }
    if (!form.cargo.trim() || !form.fechaNombramiento) {
      setError('Cargo y fecha de nombramiento obligatorios');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await actualizarMiembroOrgano(organo.id, miembro.id, {
          cargo: form.cargo.trim(),
          fechaNombramiento: form.fechaNombramiento,
          fechaCese: form.fechaCese || undefined,
          nombreExterno: form.tipo === 'externa' ? form.nombreExterno.trim() : undefined,
        });
        onSaved('Miembro actualizado');
      } else {
        const payload: CrearMiembroOrganoInput = {
          cargo: form.cargo.trim(),
          fechaNombramiento: form.fechaNombramiento,
        };
        if (form.tipo === 'interna') {
          payload.personaId = form.personaId;
        } else {
          payload.nombreExterno = form.nombreExterno.trim();
        }
        if (form.fechaCese) payload.fechaCese = form.fechaCese;
        await añadirMiembroOrgano(organo.id, payload);
        onSaved('Miembro añadido');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalStyle, width: 520 }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? 'Editar miembro' : 'Añadir miembro'}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isEdit && (
            <div>
              <label style={lblStyle}>Tipo de miembro</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => set('tipo', 'interna')}
                  style={toggleBtnStyle(form.tipo === 'interna')}
                >
                  Persona interna
                </button>
                <button
                  type="button"
                  onClick={() => set('tipo', 'externa')}
                  style={toggleBtnStyle(form.tipo === 'externa')}
                >
                  Persona externa
                </button>
              </div>
            </div>
          )}

          {form.tipo === 'interna' && !isEdit ? (
            <div>
              <label style={lblStyle}>Persona *</label>
              <select
                value={form.personaId}
                onChange={(e) => set('personaId', e.target.value)}
                style={selStyle}
              >
                <option value="">— Selecciona persona —</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} {p.apellidos}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label style={lblStyle}>
                {form.tipo === 'externa' ? 'Nombre (externo) *' : 'Nombre externo'}
              </label>
              <input
                type="text"
                value={form.nombreExterno}
                onChange={(e) => set('nombreExterno', e.target.value)}
                style={inpStyle}
                maxLength={300}
                disabled={isEdit && form.tipo === 'interna'}
              />
            </div>
          )}

          <div>
            <label style={lblStyle}>Cargo *</label>
            <input
              type="text"
              value={form.cargo}
              onChange={(e) => set('cargo', e.target.value)}
              placeholder={placeholders.cargo ?? 'Administrador Único, Consejero, Secretario...'}
              style={inpStyle}
              maxLength={200}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Fecha nombramiento *</label>
              <input
                type="date"
                value={form.fechaNombramiento}
                onChange={(e) => set('fechaNombramiento', e.target.value)}
                style={inpStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Fecha cese (opcional)</label>
              <input
                type="date"
                value={form.fechaCese}
                onChange={(e) => set('fechaCese', e.target.value)}
                style={inpStyle}
              />
            </div>
          </div>

          {error && <div style={errorBoxStyle}>{error}</div>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} disabled={saving} style={btnSecStyle}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={btnPrimStyle}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main: AdminOrganos
// ═══════════════════════════════════════════════════════════════

export function AdminOrganos() {
  const tooltips = useAdmTooltips();
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const [organos, setOrganos] = useState<OrganoGobierno[]>([]);
  const [entidades, setEntidades] = useState<EntidadLite[]>([]);
  const [personas, setPersonas] = useState<PersonaResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [organoModal, setOrganoModal] = useState<'new' | OrganoGobierno | null>(null);
  const [miembroModal, setMiembroModal] = useState<{
    organo: OrganoGobierno;
    miembro: MiembroOrganoGobierno | null;
  } | null>(null);
  const [confirmDesactivar, setConfirmDesactivar] = useState<OrganoGobierno | null>(null);
  const [confirmCesar, setConfirmCesar] = useState<OrganoMiembroPair | null>(null);
  const [confirmEliminar, setConfirmEliminar] = useState<OrganoMiembroPair | null>(null);
  const [toast, setToast] = useState('');
  const mountedRef = useRef(true);
  const toastTimerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [organosRes, entidadesRes, personasRes] = await Promise.all([
        fetchOrganosGobierno(),
        fetchEntidadesGobernanza(),
        fetchPersonas({ page: '1', pageSize: '500' }),
      ]);
      if (!mountedRef.current) return;
      setOrganos(organosRes.data ?? []);
      setEntidades(entidadesRes.data ?? []);
      setPersonas(personasRes.data ?? []);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(
        err instanceof Error ? err.message : 'No se pudieron cargar los órganos de gobierno',
      );
      setOrganos([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setToast('');
      toastTimerRef.current = null;
    }, 3000);
  };

  const handleDesactivarRequest = (organo: OrganoGobierno) => {
    setConfirmDesactivar(organo);
  };

  const handleDesactivarConfirm = async () => {
    if (!confirmDesactivar) return;
    try {
      await desactivarOrganoGobierno(confirmDesactivar.id);
      showToast('Órgano desactivado');
      load();
    } catch {
      showToast('Error al desactivar');
    } finally {
      setConfirmDesactivar(null);
    }
  };

  const handleCesarRequest = (organo: OrganoGobierno, miembro: MiembroOrganoGobierno) => {
    setConfirmCesar({ organo, miembro });
  };

  const handleCesarConfirm = async () => {
    if (!confirmCesar) return;
    try {
      await cesarMiembroOrgano(confirmCesar.organo.id, confirmCesar.miembro.id);
      showToast('Miembro cesado');
      load();
    } catch {
      showToast('Error al cesar');
    } finally {
      setConfirmCesar(null);
    }
  };

  const handleEliminarRequest = (organo: OrganoGobierno, miembro: MiembroOrganoGobierno) => {
    setConfirmEliminar({ organo, miembro });
  };

  const handleEliminarConfirm = async () => {
    if (!confirmEliminar) return;
    try {
      await eliminarMiembroOrgano(confirmEliminar.organo.id, confirmEliminar.miembro.id);
      showToast('Miembro eliminado');
      load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al eliminar');
    } finally {
      setConfirmEliminar(null);
    }
  };

  const handleToggleActivo = async (organo: OrganoGobierno) => {
    try {
      await actualizarOrganoGobierno(organo.id, { activo: !organo.activo });
      showToast(organo.activo ? 'Órgano desactivado' : 'Órgano activado');
      load();
    } catch {
      showToast('Error al cambiar estado');
    }
  };

  const q = search.toLowerCase().trim();
  const filteredOrganos = organos
    .filter((o) => (mostrarInactivos ? true : o.activo !== false))
    .filter((o) =>
      q
        ? (o.codigo ?? '').toLowerCase().includes(q) ||
          o.nombre.toLowerCase().includes(q) ||
          o.tipoOrgano.toLowerCase().includes(q) ||
          (o.entidad?.razonSocial ?? '').toLowerCase().includes(q)
        : true,
    );

  return (
    <div>
      {/* 0471 M10 — Solo el header migra al shell. El body mantiene accordion +
          modales anidados + miembros inline intactos (logica irreducible segun
          inventario M1). */}
      <AdminPageHeader
        title={(adm as any).organos ?? 'Órganos de Gobierno'}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: (adm as any).search ?? 'Buscar...',
        }}
        onNew={{
          label: (adm as any).nuevoOrgano ?? 'Nuevo órgano',
          onClick: () => setOrganoModal('new'),
          disabled: entidades.length === 0,
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {(adm as any).mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      {loading ? (
        <LoadingSpinner size="md" muted />
      ) : error ? (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: 16,
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : organos.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            color: '#94A3B8',
            background: '#fff',
            borderRadius: 10,
            border: '1px dashed #D1D5DB',
          }}
        >
          No hay órganos de gobierno registrados
        </div>
      ) : (
        <div
          style={{
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 4px rgba(15,23,42,0.07)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0E243E', color: '#fff' }}>
                <th style={{ ...thStyle, width: 100 }}>{(adm as any).colCodigo ?? 'Código'}</th>
                <th style={thStyle}>{(adm as any).nombre ?? 'Nombre'}</th>
                <th style={thStyle}>{(adm as any).tipo ?? 'Tipo'}</th>
                <th style={thStyle}>{(adm as any).entidad ?? 'Entidad'}</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 110 }}>
                  {(adm as any).miembros ?? 'Miembros'}
                </th>
                <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>
                  {(adm as any).activo ?? 'Activo'}
                </th>
                <th style={{ ...thStyle, width: 140 }}>{(adm as any).acciones ?? 'Acciones'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganos.flatMap((organo, i) => {
                const miembrosActivos = organo.miembros.filter((m) => !m.fechaCese).length;
                const expanded = expandedId === organo.id;
                return [
                  <tr
                    key={organo.id}
                    onClick={() => setExpandedId(expanded ? null : organo.id)}
                    style={{
                      background: expanded ? '#EFF6FF' : i % 2 === 0 ? '#fff' : '#FAFBFC',
                      cursor: 'pointer',
                    }}
                  >
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 12,
                        color: C.azul2,
                      }}
                    >
                      {organo.codigo && organo.codigo.length > 0 ? organo.codigo : '—'}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{organo.nombre}</td>
                    <td style={tdStyle}>{organo.tipoOrgano}</td>
                    <td style={tdStyle}>{organo.entidad.razonSocial}</td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {miembrosActivos} / {organo.miembros.length}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 10,
                          fontWeight: 600,
                          background: organo.activo ? '#F0FDF4' : '#FEF2F2',
                          color: organo.activo ? '#16A34A' : '#DC2626',
                        }}
                      >
                        {organo.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          title={tooltips.editar ?? 'Editar'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrganoModal(organo);
                          }}
                          style={actionBtnStyle}
                        >
                          <svg
                            width={14}
                            height={14}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={C.azul2}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          title={
                            organo.activo
                              ? (tooltips.toggleDesactivar ?? 'Desactivar')
                              : (tooltips.toggleActivar ?? 'Activar')
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActivo(organo);
                          }}
                          style={actionBtnStyle}
                        >
                          <svg
                            width={14}
                            height={14}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={organo.activo ? '#16A34A' : '#9CA3AF'}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 2v10" />
                            <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
                          </svg>
                        </button>
                        <button
                          title={tooltips.eliminar ?? 'Eliminar'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDesactivarRequest(organo);
                          }}
                          style={actionBtnStyle}
                        >
                          <svg
                            width={14}
                            height={14}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#DC2626"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>,
                  expanded ? (
                    <tr key={`${organo.id}-panel`}>
                      <td colSpan={7} style={{ padding: 0, background: '#F8FAFC' }}>
                        <MiembrosPanel
                          organo={organo}
                          onAdd={() => setMiembroModal({ organo, miembro: null })}
                          onEdit={(m) => setMiembroModal({ organo, miembro: m })}
                          onCesar={(m) => handleCesarRequest(organo, m)}
                          onEliminar={(m) => handleEliminarRequest(organo, m)}
                        />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
          <div
            style={{
              padding: '8px 16px',
              fontSize: 12,
              color: '#6B7280',
              borderTop: '1px solid #E5E7EB',
              background: '#fff',
            }}
          >
            {filteredOrganos.length} de {organos.length} {(adm as any).organosContador ?? 'organos'}
          </div>
        </div>
      )}

      {organoModal !== null && (
        <OrganoModal
          organo={organoModal === 'new' ? null : organoModal}
          entidades={entidades}
          onClose={() => setOrganoModal(null)}
          onSaved={(msg) => {
            setOrganoModal(null);
            load();
            showToast(msg);
          }}
        />
      )}

      {miembroModal !== null && (
        <MiembroModal
          organo={miembroModal.organo}
          miembro={miembroModal.miembro}
          personas={personas}
          onClose={() => setMiembroModal(null)}
          onSaved={(msg) => {
            setMiembroModal(null);
            load();
            showToast(msg);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDesactivar !== null}
        title={tooltips.desactivarOrgano ?? 'Desactivar órgano'}
        message={
          confirmDesactivar
            ? `¿Desactivar "${confirmDesactivar.nombre}"? El órgano dejará de aparecer en el sistema.`
            : ''
        }
        onConfirm={handleDesactivarConfirm}
        onCancel={() => setConfirmDesactivar(null)}
      />

      <ConfirmDialog
        open={confirmCesar !== null}
        title={tooltips.cesarMiembro ?? 'Cesar miembro'}
        message={
          confirmCesar
            ? `¿Cesar a "${getMiembroLabel(confirmCesar.miembro)}" como ${confirmCesar.miembro.cargo}? Se registrará la fecha de cese.`
            : ''
        }
        onConfirm={handleCesarConfirm}
        onCancel={() => setConfirmCesar(null)}
      />

      <ConfirmDialog
        open={confirmEliminar !== null}
        title={tooltips.eliminarMiembro ?? 'Eliminar miembro'}
        message={
          confirmEliminar
            ? `¿Eliminar a "${getMiembroLabel(confirmEliminar.miembro)}"? Esta acción no se puede deshacer.`
            : ''
        }
        variant="danger"
        onConfirm={handleEliminarConfirm}
        onCancel={() => setConfirmEliminar(null)}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Miembros panel (expandido)
// ═══════════════════════════════════════════════════════════════

function MiembrosPanel({
  organo,
  onAdd,
  onEdit,
  onCesar,
  onEliminar,
}: {
  organo: OrganoGobierno;
  onAdd: () => void;
  onEdit: (m: MiembroOrganoGobierno) => void;
  onCesar: (m: MiembroOrganoGobierno) => void;
  onEliminar: (m: MiembroOrganoGobierno) => void;
}) {
  const tooltips = useAdmTooltips();
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const miembros = [...organo.miembros].sort((a, b) => {
    if (!a.fechaCese && b.fechaCese) return -1;
    if (a.fechaCese && !b.fechaCese) return 1;
    return b.fechaNombramiento.localeCompare(a.fechaNombramiento);
  });

  return (
    <div style={{ padding: '14px 20px 18px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#5A6171', letterSpacing: 0.3 }}>
          MIEMBROS
        </span>
        <button
          onClick={onAdd}
          style={{
            padding: '6px 14px',
            background: '#fff',
            border: `1px solid ${C.azul2}`,
            color: C.azul2,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Añadir miembro
        </button>
      </div>

      {miembros.length === 0 ? (
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            color: '#94A3B8',
            fontSize: 12,
            background: '#fff',
            borderRadius: 6,
            border: '1px dashed #E2E6ED',
          }}
        >
          Sin miembros registrados
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            background: '#fff',
            borderRadius: 6,
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
          }}
        >
          <thead>
            <tr style={{ background: '#F1F5F9' }}>
              <th style={panelThStyle}>{(adm as any).nombre ?? 'Nombre'}</th>
              <th style={panelThStyle}>{(adm as any).cargo ?? 'Cargo'}</th>
              <th style={panelThStyle}>{(adm as any).desde ?? 'Desde'}</th>
              <th style={panelThStyle}>{(adm as any).hasta ?? 'Hasta'}</th>
              <th style={{ ...panelThStyle, textAlign: 'center', width: 80 }}>
                {(adm as any).estado ?? 'Estado'}
              </th>
              <th style={{ ...panelThStyle, width: 110 }}>{(adm as any).acciones ?? 'Acciones'}</th>
            </tr>
          </thead>
          <tbody>
            {miembros.map((miembro) => {
              const isActivo = !miembro.fechaCese;
              const nombre = miembro.persona
                ? `${miembro.persona.nombre} ${miembro.persona.apellidos}`.trim()
                : (miembro.nombreExterno ?? '—');
              return (
                <tr key={miembro.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={panelTdStyle}>
                    <span style={{ fontWeight: 600, color: '#0E243E' }}>{nombre}</span>
                    {!miembro.persona && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          color: '#94A3B8',
                          fontWeight: 500,
                        }}
                      >
                        (externo)
                      </span>
                    )}
                  </td>
                  <td style={{ ...panelTdStyle, color: '#5A6171' }}>{miembro.cargo}</td>
                  <td
                    style={{
                      ...panelTdStyle,
                      color: '#5A6171',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {miembro.fechaNombramiento?.slice(0, 10) ?? '—'}
                  </td>
                  <td
                    style={{
                      ...panelTdStyle,
                      color: '#5A6171',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {miembro.fechaCese?.slice(0, 10) ?? '—'}
                  </td>
                  <td style={{ ...panelTdStyle, textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        background: isActivo ? '#F0FDF4' : '#F3F4F6',
                        color: isActivo ? '#16A34A' : '#6B7280',
                      }}
                    >
                      {isActivo ? 'Activo' : 'Cesado'}
                    </span>
                  </td>
                  <td style={panelTdStyle}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        title={tooltips.editar ?? 'Editar'}
                        onClick={() => onEdit(miembro)}
                        style={actionBtnStyle}
                      >
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={C.azul2}
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      {isActivo && (
                        <button
                          title={tooltips.cesar ?? 'Cesar'}
                          onClick={() => onCesar(miembro)}
                          style={actionBtnStyle}
                        >
                          <svg
                            width={12}
                            height={12}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#C2410C"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                        </button>
                      )}
                      {isActivo && (
                        <button
                          title={tooltips.eliminar ?? 'Eliminar'}
                          onClick={() => onEliminar(miembro)}
                          style={actionBtnStyle}
                        >
                          <svg
                            width={12}
                            height={12}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#DC2626"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Shared styles
// ═══════════════════════════════════════════════════════════════

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  borderTop: `3px solid ${C.azul1}`,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: '24px 28px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid #F3F4F6',
};

const panelThStyle: React.CSSProperties = {
  padding: '7px 10px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: '#5A6171',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

const panelTdStyle: React.CSSProperties = {
  padding: '7px 10px',
  fontSize: 12,
};

const lblStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const inpStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const selStyle: React.CSSProperties = {
  ...inpStyle,
  background: '#fff',
  appearance: 'auto',
};

const btnSecStyle: React.CSSProperties = {
  padding: '9px 18px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  background: '#fff',
  color: '#374151',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnPrimStyle: React.CSSProperties = {
  padding: '9px 22px',
  background: C.azul2,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#FEF2F2',
  color: '#B91C1C',
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 12,
};

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 6px',
  border: '1px solid #E5E7EB',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toggleBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  border: `1px solid ${active ? C.azul2 : '#D1D5DB'}`,
  borderRadius: 6,
  background: active ? '#EFF6FF' : '#fff',
  color: active ? C.azul2 : '#374151',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  flex: 1,
});
